"use client"

import { useEffect, useState } from "react"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { AnimatePresence, m } from "framer-motion"
import { useCountry } from "@/components/layout/country-provider"
import { Button } from "@/components/ui/button"
import type { Answers } from "@/lib/quiz/types"
import type { InsurersData } from "@/lib/quiz/insurers"
import type { PriceMap } from "@/lib/pricing"
import { recommend } from "@/lib/quiz/recommend"
import { totalPrice } from "@/lib/quiz/product-mapping"
import { PackageComparison } from "./package-comparison"
import { CoverageBand } from "./coverage-band"
import { BloodTestAltCard } from "./blood-test-alt-card"
import { ProFormaModal } from "./pro-forma-modal"
import type { ReviewerExpert } from "@/lib/reviewer-experts"
import type { QuizMarket } from "@/lib/quiz/market"

/** Revert / "retake" arrow — Lucide RotateCcw glyph, drawn inline so
 *  we don't pull a fresh icon dep just for the result page footer. */
function RotateCcwIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <polyline points="3 3 3 8 8 8" />
    </svg>
  )
}

interface Props {
  answers: Answers
  prices: PriceMap
  productIds: PriceMap
  insurers: InsurersData
  experts: ReviewerExpert[]
  market: QuizMarket
  /**
   * "focus" lifts the result block closer to the top of the viewport
   * and animates the header upward as soon as the recommendation is
   * ready, smoothing the transition from the loading spinner to the
   * full package card.
   */
  isFocus?: boolean
}

export function ResultPage({
  answers,
  prices,
  productIds,
  insurers,
  market,
  isFocus = false,
}: Props) {
  const tQuiz = useTranslations("quiz")
  const t = useTranslations("quiz.result")
  const { code: country } = useCountry()
  // The real locale — used for navigation. Do NOT narrow this: the previous
  // `en|fr ? locale : "de"` narrowing was a leftover from the coverage lookup
  // and silently sent every other locale to `/{country}/de`, i.e. `/nl/de`,
  // which doesn't exist (AHD-9269).
  const locale = useLocale()
  const [loaded, setLoaded] = useState(false)
  const [proFormaOpen, setProFormaOpen] = useState(false)
  const recommendation = recommend(answers, market)
  const total = totalPrice(
    prices,
    recommendation.baseTier,
    recommendation.addOns,
    answers.gender
  )
  useEffect(() => {
    const id = setTimeout(() => setLoaded(true), 1500)
    return () => clearTimeout(id)
  }, [])

  // Lifted closer to the top in focus mode — the result page is a long
  // scroll, so anchoring it near the chrome bottom (instead of at the
  // section's default 18vh top padding) puts the package card above
  // the fold without dead space above. The header smoothly slides up
  // from a more-centered loading position to its final spot above the
  // recommendation.
  const header = (
    <m.header
      initial={false}
      animate={{ y: loaded ? 0 : 48, opacity: 1 }}
      transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
      className={isFocus ? "mb-8 text-center" : "mb-10"}
    >
      <h1 className="type-display-md text-neutral-900">
        {tQuiz("resultHeader.title")}
      </h1>
      <p className="type-body-lg mt-3 text-neutral-600">
        {tQuiz("resultHeader.subtitle")}
      </p>
    </m.header>
  )

  return (
    <div className={isFocus ? "-mt-[12vh] space-y-8" : "space-y-8"}>
      {header}

      {/* safe-animate-presence: gated by `loaded` boolean state */}
      <AnimatePresence mode="wait" initial={false}>
        {!loaded ? (
          <m.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col items-center justify-center py-16"
          >
            <div className="border-deep-blue-200 border-t-deep-blue-900 h-12 w-12 animate-spin rounded-full border-4" />
            <p className="type-title-md mt-6 text-neutral-700">
              {t("loading")}
            </p>
          </m.div>
        ) : (
          <m.div
            key="loaded"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.55,
              ease: [0.16, 1, 0.3, 1],
              delay: 0.05,
            }}
            className="space-y-8"
          >
            <PackageComparison
              recommendation={recommendation}
              answers={answers}
              prices={prices}
              productIds={productIds}
              insurers={insurers}
              market={market}
            />

            <BloodTestAltCard
              baseTier={recommendation.baseTier}
              prices={prices}
              market={market}
            />

            {/* Insurer reimbursement + pro-forma are CH-only (D1): there is
              no Dutch insurance story and DE is pure self-pay. Note the
              no-insurer branch of CoverageBand is not a no-op — it renders a
              "coverage unclear" card plus the pro-forma link — so this has to
              be gated at the mount, not left to degrade. */}
            {market.insurance && (
              <CoverageBand
                baseTier={recommendation.baseTier}
                insurers={insurers}
                insurerId={answers.insurer_id}
                insurerPackageId={answers.insurer_package_id}
                onRequestProForma={() => setProFormaOpen(true)}
              />
            )}

            {/* Footer — low-emphasis "Back to home" text link on the
              left, "Retake quiz" outlined button (with revert icon)
              on the right. Anything more discovery-oriented belongs
              back on the home surface, not stapled to a recommendation. */}
            <div className="flex items-center justify-between pt-2">
              <Button
                variant="text"
                onClick={() => {
                  window.location.href = `/${country}/${locale}`
                }}
              >
                {t("backToHome")}
              </Button>
              <Button
                variant="outline"
                iconLeft={<RotateCcwIcon />}
                onClick={() => {
                  // Hard-reload the focused quiz route so the wizard's
                  // local state resets — useState in `Wizard` is the
                  // canonical source of step + answers, and a fresh
                  // mount is the only way to clear it.
                  window.location.href = `/${country}/${locale}/quiz-focus`
                }}
              >
                {t("retakeQuiz")}
              </Button>
            </div>
          </m.div>
        )}
      </AnimatePresence>

      {market.insurance && (
        <ProFormaModal
          open={proFormaOpen}
          onClose={() => setProFormaOpen(false)}
          answers={answers}
          recommendation={recommendation}
          totalPriceChf={total}
          prices={prices}
        />
      )}
    </div>
  )
}
